import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card } from "antd";
import { listPlacement } from "./api";
import BannerForm from "./BannerForm";
import HomeBannersLayout from "./HomeBannersLayout";
import { notify } from "./notify";
import PhonePreview from "./PhonePreview";
import PlacementTabs from "./PlacementTabs";
import { placementById, placementsOn } from "./placements";
import SectionForm from "./SectionForm";
import SectionList from "./SectionList";
import StripList from "./StripList";
import useFranchiseChoice from "./useFranchiseChoice";

const chosenPlacement = (query, screen) => {
  const asked = placementById(query.get("placement"));
  return asked && asked.screen === screen ? asked : placementsOn(screen)[0];
};

export default function HomeBanners() {
  const history = useHistory();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const setQuery = (changes) => {
    const next = new URLSearchParams(location.search);
    Object.entries(changes).forEach(([key, value]) => next.set(key, value));
    history.replace(`/home-banners?${next.toString()}`);
  };
  const screen = query.get("screen") === "surplus" ? "surplus" : "home";
  const placement = chosenPlacement(query, screen);
  const strip = placement.shape === "strip";
  const { franchiseId, picker, ready } = useFranchiseChoice(query, setQuery);
  const [reply, setReply] = useState(null);
  const [editing, setEditing] = useState(null);
  const [version, setVersion] = useState(0);

  const load = useCallback(async () => {
    if (!ready) return;
    const result = await listPlacement(placement.id, franchiseId);
    if (result.Status === true) setReply(result);
    else notify(result);
    setVersion((v) => v + 1);
  }, [placement.id, franchiseId, ready]);

  useEffect(() => {
    setReply(null);
    load();
  }, [load]);

  const rows = reply && reply.placement === placement.id ? reply : null;
  const formProps = { placement: placement.id, franchiseId, onClose: () => setEditing(null), onSaved: () => { setEditing(null); load(); } };

  return (
    <HomeBannersLayout>
      <div className="d-flex align-items-center justify-content-between flex-wrap mb-3" style={{ gap: 12 }}>
        <div>
          <h3 className="mb-0">Home banners</h3>
          <span className="text-muted">Pictures on the customer app's home and Surplus screens, each opening one product or restaurant.</span>
        </div>
        {picker}
      </div>
      {!ready ? (
        <div className="alert alert-info">Pick a franchise to manage its banners.</div>
      ) : (
        <div className="row">
          <div className="col-xl-8 col-lg-7">
            <PlacementTabs screen={screen} placement={placement} onChange={setQuery} />
            <Card
              title={placement.label}
              extra={
                <Button type="primary" onClick={() => setEditing({ kind: strip ? "banner" : "section" })}>
                  {strip ? "Add banner" : "Add section"}
                </Button>
              }
            >
              {rows && strip && (
                <StripList banners={rows.banners || []} placement={placement.id} franchiseId={franchiseId} onChanged={load}
                  onEdit={(banner) => setEditing({ kind: "banner", row: banner })} />
              )}
              {rows && !strip && (
                <SectionList sections={rows.sections || []} franchiseId={franchiseId} history={history} onChanged={load}
                  onEdit={(section) => setEditing({ kind: "section", row: section })} />
              )}
            </Card>
          </div>
          <div className="col-xl-4 col-lg-5">
            <PhonePreview franchise={rows && rows.franchise} screen={screen} highlightPlacement={placement.id} version={version} />
          </div>
        </div>
      )}
      <BannerForm open={Boolean(editing && editing.kind === "banner")} banner={editing && editing.row} {...formProps} />
      <SectionForm open={Boolean(editing && editing.kind === "section")} section={editing && editing.row} {...formProps} />
    </HomeBannersLayout>
  );
}
